/**
 * Zustand store for TikTok Listing inline edit dirty state.
 *
 * Scoped to the detail page — tracks which fields the user has changed,
 * their original values, and save status. The FloatingSavePill reads
 * from this store to decide visibility and call the save API.
 *
 * Global singleton — `init()` resets when productId changes.
 */

import { create } from "zustand";
import type { TikTokImage } from "../api/tiktokProductApi";
import { parseJsonField } from "../widgets/shared";

/**
 * Parse TikTok main_images JSON into normalized array.
 * Input format: [{urls: ["cdn1", ...], uri: "...", width: N, height: N}, ...]
 * Output format: [{uri, url, width, height}, ...]
 */
function extractTikTokImages(raw: unknown): TikTokImage[] {
  const parsed = parseJsonField<unknown[]>(raw);
  if (!parsed || !Array.isArray(parsed)) return [];
  return parsed
    .filter((item): item is Record<string, any> => typeof item === "object" && item !== null)
    .map((item) => ({
      uri: item.uri || "",
      url: (Array.isArray(item.urls) ? item.urls[0] : item.url) || "",
      width: Number(item.width || 0),
      height: Number(item.height || 0),
    }));
}

/**
 * Extract category_id from category_chains JSON.
 * category_chains is [{id, parent_id, local_name, is_leaf}, ...] — the leaf is last.
 */
function extractCategoryId(raw: unknown): string {
  const parsed = parseJsonField<unknown[]>(raw);
  if (!parsed || !Array.isArray(parsed) || parsed.length === 0) return "";
  const last = parsed[parsed.length - 1];
  if (typeof last === "object" && last !== null && "id" in last) {
    return String((last as Record<string, any>).id);
  }
  return "";
}

export interface TikTokEditState {
  /** Original field values snapshot (set once per listing) */
  originalValues: Record<string, any>;
  /** Only fields that differ from original */
  dirtyFields: Record<string, any>;
  /** Marketplace Connection doc name */
  connectionName: string;
  /** TikTok product_id */
  productId: string;
  /** Frappe TikTok Listing doc name (for React Query cache invalidation) */
  listingName: string;
  /** Save lifecycle */
  saveStatus: "idle" | "saving" | "success" | "error";
  saveMessage: string;
  /** Guard: which productId was last initialized */
  _initializedFor: string;
  /** Validation errors for required fields (key = field path, value = error message) */
  validationErrors: Record<string, string>;
  /** Raw formData reference for transformer (not a dirty-tracked field) */
  _formData: Record<string, any>;

  // Actions
  init: (
    formData: Record<string, any>,
    connectionName: string,
    productId: string,
    listingName: string,
  ) => void;
  setField: (key: string, value: any) => void;
  resetAll: () => void;
  setSaveStatus: (status: TikTokEditState["saveStatus"], message?: string) => void;
  /** Clear init guard so store re-initializes with fresh formData after save */
  forceReinit: () => void;
  /** Set a validation error for a specific field */
  setValidationError: (key: string, message: string) => void;
  /** Clear a specific validation error */
  clearValidationError: (key: string) => void;
  /** Clear all validation errors (e.g., on category change or unmount) */
  clearAllValidationErrors: () => void;
}

/** Parse a numeric value safely (NaN → 0) */
function flt(v: unknown): number {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

/**
 * Deep-equal check for primitive values and simple objects/arrays.
 * Used to avoid marking a field dirty when value hasn't actually changed.
 */
function valuesEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

export const useTikTokEditStore = create<TikTokEditState>((set, get) => ({
  originalValues: {},
  dirtyFields: {},
  connectionName: "",
  productId: "",
  listingName: "",
  saveStatus: "idle",
  saveMessage: "",
  _initializedFor: "",
  validationErrors: {},
  _formData: {},

  init(formData, connectionName, productId, listingName) {
    const state = get();
    // Skip if already initialized for this exact product
    if (state._initializedFor === productId && productId) return;

    // Extract the field values we care about editing
    const originals: Record<string, any> = {
      title: formData.title ?? "",
      description: formData.description ?? "",
      video_url: formData.video_url ?? "",
      package_weight: formData.package_weight ?? 0,
      package_dimensions: formData.package_dimensions ?? "{}",
      is_cod_allowed: formData.is_cod_allowed ?? 0,
      main_images: extractTikTokImages(formData.main_images),
      category_id: extractCategoryId(formData.category_chains),
      product_attributes: parseJsonField<unknown[]>(formData.product_attributes) ?? [],
      price: flt(formData.price ?? 0),
      sale_price: flt(formData.sale_price ?? 0),
      inventory_quantity: Number(formData.inventory_quantity ?? 0),
      brand_id: formData.brand_id ?? "",
      brand_name: formData.brand_name ?? "",
    };

    set({
      originalValues: originals,
      dirtyFields: {},
      connectionName,
      productId,
      listingName,
      saveStatus: "idle",
      saveMessage: "",
      _initializedFor: productId,
      validationErrors: {},
      _formData: formData,
    });
  },

  setField(key, value) {
    const state = get();
    const original = state.originalValues[key];

    // If value matches original, remove from dirty (not actually changed)
    if (valuesEqual(value, original)) {
      const next = { ...state.dirtyFields };
      delete next[key];
      set({ dirtyFields: next });
    } else {
      set({ dirtyFields: { ...state.dirtyFields, [key]: value } });
    }
  },

  resetAll() {
    set({
      dirtyFields: {},
      saveStatus: "idle",
      saveMessage: "",
      validationErrors: {},
    });
  },

  setSaveStatus(status, message) {
    set({ saveStatus: status, saveMessage: message ?? "" });
  },

  forceReinit() {
    set({ _initializedFor: "" });
  },

  setValidationError(key, message) {
    set({ validationErrors: { ...get().validationErrors, [key]: message } });
  },

  clearValidationError(key) {
    const next = { ...get().validationErrors };
    delete next[key];
    set({ validationErrors: next });
  },

  clearAllValidationErrors() {
    set({ validationErrors: {} });
  },
}));

// ── Selectors (use these in components) ──────────────────────────────

/** Whether any field has been changed */
export const selectIsDirty = (s: TikTokEditState) =>
  Object.keys(s.dirtyFields).length > 0;

/** Number of dirty fields (for badge) */
export const selectDirtyCount = (s: TikTokEditState) =>
  Object.keys(s.dirtyFields).length;

/** Get the current value of a field (dirty or original) */
export function selectFieldValue(key: string) {
  return (s: TikTokEditState) =>
    key in s.dirtyFields ? s.dirtyFields[key] : s.originalValues[key];
}

/** Whether a specific field is dirty */
export function selectIsFieldDirty(key: string) {
  return (s: TikTokEditState) => key in s.dirtyFields;
}

/** Whether there are any validation errors blocking save */
export const selectHasValidationErrors = (s: TikTokEditState) =>
  Object.keys(s.validationErrors).length > 0;

/** Statuses that make the product fully read-only (user cannot edit) */
const READ_ONLY_STATUSES = new Set([
  "freeze", "platform_deactivated", "deleted",
]);

/** Whether the product is in a read-only state (frozen, platform-deactivated, deleted) */
export function selectIsReadOnly(s: TikTokEditState) {
  const status = (s._formData?.status as string || "").toLowerCase();
  return READ_ONLY_STATUSES.has(status);
}
